import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { BackIcon, CheckIcon } from '../components/Brand/Icon';
import { appLocalStorage } from '../utils/storage';
import config from '../utils/config';
import url from '../utils/url';
import { APP_ROUTES } from '../utils/constant';
import appLinks from '../appLinks/config.json';
import { LEGAL_VERSION, registerLegalPresenter } from './consent';
import { legalCopy } from './copy';
import './consent.scss';
const endpoint = `${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/user/legal-consent`;

export default function LegalConsentProvider() {
  const { i18n } = useTranslation('brand', { useSuspense: false });
  const copy = legalCopy(i18n.language);
  const [queue, setQueue] = useState([]);
  const [choice, setChoice] = useState({ terms: false, opinions: false, ai: false });
  const [loading, setLoading] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const current = queue[0];
  const currentRef = useRef(current), saveRequest = useRef(null);
  currentRef.current = current;
  const loaded = Boolean(current && loadedFor === current);
  const finish = useCallback((item, value) => { item?.resolve(value); setQueue(previous => previous.filter(entry => entry !== item)); }, []);
  useEffect(() => {
    const pending = new Set();
    const unregister = registerLegalPresenter(options => new Promise(resolve => {
      const item = { ...options, resolve: value => { pending.delete(item); resolve(value); } };
      pending.add(item); setQueue(previous => [...previous, item]);
    }));
    return () => {
      unregister();
      saveRequest.current?.controller.abort();
      saveRequest.current = null;
      for (const item of pending) item.resolve(false);
    };
  }, []);
  useEffect(() => {
    if (!current) return;
    // A queued upload can be cancelled before it becomes the active request;
    // subscribing now would miss its already-dispatched abort event.
    if (current.signal?.aborted || current.token !== appLocalStorage.getItem(config.token)) {
      finish(current, false);
      return;
    }
    const controller = new AbortController();
    const owns = () => !controller.signal.aborted && current.token === appLocalStorage.getItem(config.token);
    const cancel = () => { controller.abort(); finish(current, false); };
    current.signal?.addEventListener('abort', cancel, { once: true });
    const timer = setInterval(() => { if (!owns()) cancel(); }, 500);
    setLoading(true); setLoadedFor(null); setError(false); setSaving(false);
    axios.get(endpoint, { headers: { [config.token]: current.token }, signal: controller.signal, timeout: 15000 })
      .then(response => {
        if (!owns()) return;
        if (response.data?.version !== LEGAL_VERSION || !response.data.consent) throw new Error();
        const fresh = response.data.consent;
        setLoadedFor(current);
        setChoice({ terms: fresh.terms === true, opinions: fresh.opinions === true, ai: fresh.ai === true });
      }).catch(() => { if (owns()) setError(true); }).finally(() => { if (owns()) setLoading(false); });
    return () => {
      controller.abort(); clearInterval(timer); current.signal?.removeEventListener('abort', cancel);
      if (saveRequest.current?.item === current) {
        saveRequest.current.controller.abort();
        saveRequest.current = null;
      }
    };
  }, [current, finish]);
  const save = async () => {
    if (!current || saveRequest.current || saving || !loaded || loading || current.token !== appLocalStorage.getItem(config.token)) return;
    const operation = { item: current, controller: new AbortController() };
    saveRequest.current = operation;
    const owns = () => saveRequest.current === operation && currentRef.current === current
      && !operation.controller.signal.aborted && current.token === appLocalStorage.getItem(config.token);
    setSaving(true); setError(false);
    try {
      const response = await axios.put(endpoint, { version: LEGAL_VERSION, ...choice }, {
        headers: { [config.token]: current.token }, timeout: 15000, signal: operation.controller.signal,
      });
      if (!owns()) return;
      if (!response.data?.success) throw new Error();
      finish(current, true);
    } catch { if (owns()) setError(true); }
    finally {
      // This provider survives account switches. An old request must not
      // unlock or add an error to the next account's choices.
      if (owns()) setSaving(false);
      if (saveRequest.current === operation) saveRequest.current = null;
    }
  };
  if (!current) return null;
  return <Modal open={Boolean(current)} title={copy.title} centered width={520} footer={null} closable={false}
    maskClosable={!saving} keyboard={!saving} onCancel={() => finish(current, false)} className="legal-consent-modal" wrapClassName="legal-consent-overlay">
    <div className="legal-consent-body" aria-busy={loading || saving}>
      {loading || (!loaded && !error) ? <Spin /> : <>
        {['terms', 'opinions', 'ai'].map(key =>
          <label className="legal-consent-choice" key={key}>
            <input type="checkbox" checked={choice[key]} disabled={saving} onChange={event => setChoice(previous => ({ ...previous, [key]: event.target.checked }))} />
            <span>{copy[key]}</span>
          </label>)}
        <p className="legal-consent-note">{copy.note}</p>
        <p>{['terms', 'privacy'].map(key => <a key={key} href={`${appLinks.origin}${url(`${APP_ROUTES.STATIC}/${key}`)}`} target="_blank" rel="noopener noreferrer">{copy[`${key}Link`]}</a>)}</p>
      </>}
      {error && <p role="alert">{copy.error}</p>}
    </div>
    <div className="legal-consent-actions">
      <button type="button" aria-label={copy.cancel} disabled={saving} onClick={() => finish(current, false)}><BackIcon /></button>
      <button type="button" aria-label={copy.save} disabled={!loaded || loading || saving} onClick={save}>{saving ? <Spin size="small" /> : <CheckIcon />}</button>
    </div>
  </Modal>;
}
